import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, fmtDateLong, fmtTime, payLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, MatchBadge } from "@/components/StatusPill";
import { MatchReasons } from "@/components/ShiftCard";
import { MapPin, Phone, Mail, Building2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { CompensationNotice } from "@/components/Notices";
import ReportDialog from "@/components/ReportDialog";

const Section = ({ title, items }) =>
  items?.length ? (
    <div>
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
        {items.map((i) => (
          <li key={i} className="flex gap-2">
            <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
            {i}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

export default function ShiftDetail() {
  const { shiftId } = useParams();
  const { user } = useAuth();
  const [shift, setShift] = useState(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/shifts/${shiftId}`);
    setShift(data);
  }, [shiftId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, success) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      setMsg(success);
      await load();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!shift) return <p className="text-sm text-slate-500">Loading shift…</p>;

  const isWorker = user?.role === "worker";
  const req = shift.my_request;
  const confirmedForMe = shift.confirmed_worker_id === user?.user_id;

  return (
    <div className="mx-auto max-w-3xl" data-testid="shift-detail">
      <Link to={isWorker ? "/worker/opportunities" : "/pharmacy/staffing"} className="inline-flex items-center gap-2 text-sm text-slate-500">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="overline">{shift.coverage_type} coverage</p>
          <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            {shift.worker_role} Coverage
          </h1>
          <p className="mt-2 flex items-center gap-2 text-base text-slate-600">
            <MapPin className="h-4 w-4" /> {shift.location_label || shift.city}
            {shift.distance != null && <span className="text-slate-400">· {shift.distance} miles away</span>}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusPill status={shift.status} />
          {shift.match_score != null && <MatchBadge score={shift.match_score} />}
        </div>
      </div>

      <div className="card-surface mt-7 grid gap-5 p-6 sm:grid-cols-3">
        <div>
          <p className="overline">Date</p>
          <p className="mt-1.5 font-display text-lg font-medium">{fmtDateLong(shift.date)}</p>
        </div>
        <div>
          <p className="overline">Hours</p>
          <p className="mt-1.5 font-display text-lg font-medium">
            {fmtTime(shift.start_time)} – {fmtTime(shift.end_time)}
          </p>
        </div>
        <div>
          <p className="overline">Pay</p>
          <p className="mt-1.5 font-display text-2xl font-semibold text-brand">{payLabel(shift)}</p>
        </div>
      </div>

      <div className="mt-7 grid gap-7 sm:grid-cols-2">
        <Section title="Requirements" items={shift.requirements} />
        <Section title="Responsibilities" items={shift.responsibilities} />
        <Section title="Pharmacy environment" items={shift.environment} />
        <Section title="Software" items={shift.software_required} />
        <Section title="Languages" items={shift.languages_required} />
      </div>

      {shift.notes && (
        <div className="card-surface mt-7 p-6">
          <h3 className="font-display text-lg font-semibold">Additional information</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">"{shift.notes}"</p>
        </div>
      )}

      <div className="card-surface mt-7 p-6">
        <h3 className="flex flex-wrap items-center gap-2 font-display text-lg font-semibold">
          <Building2 className="h-4 w-4 text-brand" /> {shift.pharmacy?.name}
          {shift.pharmacy?.npi_verified && (
            <span className="pill border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="npi-verified-pill">
              NPI verified
            </span>
          )}
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          {shift.pharmacy?.pharmacy_type} · {shift.pharmacy?.city}, {shift.pharmacy?.state}{" "}
          {shift.pharmacy?.zip_code}
        </p>
        {shift.pharmacy?.name_hidden && (
          <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="pharmacy-hidden-notice">
            This pharmacy posts in hide mode. You can see the city, ZIP code and all shift requirements now —
            the pharmacy name, address and contact details unlock once the shift is confirmed.
          </p>
        )}
        {shift.pharmacy?.description && <p className="mt-3 text-sm text-slate-600">{shift.pharmacy.description}</p>}
        {shift.pharmacy?.phone ? (
          <div className="mt-4 flex flex-wrap gap-5 text-sm text-slate-700" data-testid="pharmacy-contact">
            <span className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {shift.pharmacy.phone}</span>
            <span className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {shift.pharmacy.email}</span>
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {shift.pharmacy.address}</span>
          </div>
        ) : (
          <p className="mt-4 text-xs text-slate-400">
            Contact details unlock once the shift is confirmed for both sides.
          </p>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-100 pt-4">
          <ReportDialog
            targetType="shift"
            targetId={shift.shift_id}
            label="this shift"
            testIdPrefix="report-shift"
          />
          {shift.pharmacy?.pharmacy_id && (
            <ReportDialog
              targetType="pharmacy"
              targetId={shift.pharmacy.pharmacy_id}
              blockPharmacyId={shift.pharmacy.pharmacy_id}
              label="this pharmacy"
              testIdPrefix="report-pharmacy"
            />
          )}
        </div>
      </div>

      {isWorker && !confirmedForMe && (
        <div className="mt-6">
          <CompensationNotice />
        </div>
      )}

      {shift.match_reasons && (
        <div className="card-surface mt-7 p-6">
          <h3 className="font-display text-lg font-semibold">Why this is a {shift.match_score}% match</h3>
          <div className="mt-3">
            <MatchReasons reasons={shift.match_reasons} />
          </div>
        </div>
      )}

      {shift.confirmed_worker && (
        <div className="card-surface mt-7 p-6" data-testid="confirmed-worker-panel">
          <h3 className="font-display text-lg font-semibold">Confirmed worker</h3>
          <p className="mt-2 text-sm text-slate-700">
            {shift.confirmed_worker.full_name} · {shift.confirmed_worker.phone} · {shift.confirmed_worker.email}
          </p>
        </div>
      )}

      {error && <p className="mt-6 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="shift-action-error">{error}</p>}
      {msg && (
        <p className="mt-6 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="shift-action-message">
          <CheckCircle2 className="h-4 w-4" /> {msg}
        </p>
      )}

      {isWorker && (
        <div className="sticky bottom-4 mt-8 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white/95 p-4 backdrop-blur">
          {confirmedForMe ? (
            <span className="pill border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="worker-shift-confirmed">
              Confirmed — see you there
            </span>
          ) : req && req.status === "pending" ? (
            <>
              <button
                className="btn-primary"
                disabled={busy}
                data-testid="accept-request"
                onClick={() => act(() => api.post(`/requests/${req.request_id}/accept`), "Shift confirmed.")}
              >
                Accept
              </button>
              <button
                className="btn-ghost"
                disabled={busy}
                data-testid="decline-request"
                onClick={() => act(() => api.post(`/requests/${req.request_id}/decline`), "Request declined.")}
              >
                Decline
              </button>
              <span className="text-sm text-slate-500">The pharmacy would like you to cover this shift.</span>
            </>
          ) : shift.already_interested ? (
            <>
              <span className="pill border-purple-200 bg-purple-50 text-purple-800" data-testid="waiting-for-pharmacy">
                Waiting for pharmacy
              </span>
              <button
                className="btn-ghost"
                disabled={busy}
                data-testid="withdraw-interest"
                onClick={() => act(() => api.delete(`/shifts/${shift.shift_id}/interest`), "Interest withdrawn.")}
              >
                Withdraw availability
              </button>
            </>
          ) : (
            <button
              className="btn-accent text-base"
              disabled={busy || ["Confirmed", "Completed", "Cancelled"].includes(shift.status)}
              data-testid="im-available"
              onClick={() =>
                act(() => api.post(`/shifts/${shift.shift_id}/interest`), "Your availability has been sent to the pharmacy.")
              }
            >
              I'm available
            </button>
          )}
        </div>
      )}

      {user?.role === "pharmacy" && (
        <div className="mt-8">
          <Link to={`/pharmacy/shifts/${shift.shift_id}`} className="btn-primary" data-testid="view-matches-link">
            View matching workers ({shift.interest_count} interested)
          </Link>
        </div>
      )}
    </div>
  );
}
